/* eslint-disable i18next/no-literal-string */

import DOMPurify from 'dompurify';

// Own instance so hooks added to the shared DOMPurify (for example by the advisor chat) never change note output.
const purifier = DOMPurify(window);

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
  'ol', 'ul', 'li', 'a', 'span', 'sub', 'sup', 'img',
  // Quill 2 wraps code blocks in div.ql-code-block-container > div.ql-code-block.
  'div',
];
const ALLOWED_ATTR = ['href', 'class', 'rel', 'target', 'data-list', 'src', 'alt', 'style'];
const FORBID_TAGS = ['form', 'input', 'button', 'textarea', 'select'];
const FORBID_ATTR = ['id', 'name'];
const ALLOWED_STYLE_PROPERTIES = ['color', 'background-color', 'text-align'];
const UNSAFE_STYLE_VALUE = /url\(|expression|[\\@]|\/\*/i;

function filterStyle(style: string): string {
  return style
    .split(';')
    .map((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator < 0) {
        return null;
      }
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if (!ALLOWED_STYLE_PROPERTIES.includes(property) || !value || UNSAFE_STYLE_VALUE.test(value)) {
        return null;
      }
      return `${property}: ${value};`;
    })
    .filter((declaration): declaration is string => declaration !== null)
    .join(' ');
}

purifier.addHook('afterSanitizeAttributes', (node: Element) => {
  if (node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
  if (node.hasAttribute('class')) {
    // Only Quill formatting classes. App and Bootstrap classes such as fixed-top must not apply.
    const classes = (node.getAttribute('class') ?? '').split(/\s+/).filter(name => name.startsWith('ql-'));
    if (classes.length) {
      node.setAttribute('class', classes.join(' '));
    } else {
      node.removeAttribute('class');
    }
  }
  if (node.hasAttribute('style')) {
    const style = filterStyle(node.getAttribute('style') ?? '');
    if (style) {
      node.setAttribute('style', style);
    } else {
      node.removeAttribute('style');
    }
  }
});

export function sanitizeNoteHtml(html: string): string {
  return purifier.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}
